import React, { useState } from 'react'
import { Box, Typography, Snackbar, Alert } from '@mui/material'
import PlainModal from './modal/PlainModal'
import { IconChecks, IconCopy } from '@tabler/icons-react'
import { getWebOrigin } from '../helpers/referral-helper'
import { errorLog } from '../utils/log-helper'

interface ShareInviteProps {
  open: boolean
  handleClose: () => void
  url: string
}

const shareOptions = [
  {
    label: 'Whatsapp',
    icon: '/images/share/whatsapp.png',
    getUrl: (url: string) => `https://wa.me/?text=${encodeURIComponent(`Join me on Resactly! ${url}`)}`,
    handleShare: async (url: string, shareUrl: string) => {
      // Use Web Share API on mobile if available
      if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        try {
          await navigator.share({
            title: 'Join Resactly',
            text: `Join me on Resactly! ${shareUrl}`,
            url: shareUrl,
          })
          return true
        } catch (error) {
          // User cancelled or error occurred, fall through to window.open
        }
      }
      // Fallback to WhatsApp web URL
      window.open(url, '_blank', 'noopener,noreferrer')
      return false
    },
  },
  {
    label: 'Facebook',
    icon: '/images/share/facebook.png',
    getUrl: (url: string) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    handleShare: async (url: string) => {
      // Use Web Share API on mobile if available
      if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        try {
          await navigator.share({
            title: 'Join Resactly',
            text: 'Join me on Resactly!',
            url,
          })
          return true
        } catch (error) {
          // User cancelled or error occurred, fall through to window.open
        }
      }
      // Fallback to Facebook share URL
      window.open(url, '_blank', 'noopener,noreferrer')
      return false
    },
  },
  {
    label: 'Instagram',
    icon: '/images/share/instagram.png',
    getUrl: () => 'https://www.instagram.com/resactly/',
    handleShare: async (url: string, shareUrl: string) => {
      // Instagram doesn't support direct web share deep links
      // Copy link to clipboard and show instruction
      try {
        await navigator.clipboard.writeText(shareUrl)
        return true // Signal that we handled it (will show toast)
      } catch (error) {
        // Fallback if clipboard API fails
        return false
      }
    },
  },
  {
    label: 'LinkedIn',
    icon: '/images/share/linkedin.png',
    getUrl: (url: string) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url,
      )}`,
    handleShare: async (url: string) => {
      // Use Web Share API on mobile if available
      if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        try {
          await navigator.share({
            title: 'Join Resactly',
            text: 'Join me on Resactly!',
            url,
          })
          return true
        } catch (error) {
          // User cancelled or error occurred, fall through to window.open
        }
      }
      // Fallback to LinkedIn share URL
      window.open(url, '_blank', 'noopener,noreferrer')
      return false
    },
  },
]

const ShareInvite: React.FC<ShareInviteProps> = ({
  open,
  handleClose,
  url,
}) => {
  // url prop should be the full canonical referral URL (e.g., https://app.com/r/CODE)
  // If not provided, fall back to the canonical web origin — NOT window.location.origin, which is
  // capacitor://localhost on native and would produce an unshareable link.
  const fallbackUrl = getWebOrigin()
  const shareUrl = url?.length ? url : fallbackUrl

  const [isLinkCopy, setIsLinkCopy] = useState(false)
  const [showInstagramToast, setShowInstagramToast] = useState(false)

  const copyCurrentUrl = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setIsLinkCopy(true)
      setTimeout(() => setIsLinkCopy(false), 1000)
    } catch (error) {
      // Fallback if clipboard API fails
      errorLog('Failed to copy to clipboard', error)
    }
  }

  const handleShareClick = async (option: typeof shareOptions[0]): Promise<void> => {
    const shareUrlForOption = option.getUrl(shareUrl)
    const handled = await option.handleShare(shareUrlForOption, shareUrl)
    
    if (handled && option.label === 'Instagram') {
      // Show toast for Instagram
      setShowInstagramToast(true)
    }
  }

  return (
    <PlainModal
      open={open}
      handleClose={handleClose}
      modelWidth="500px"
      title="Share invite link"
    >
      <Box>
        <Box display="flex" gap={2} justifyContent="space-between" my={4}>
          {shareOptions.map((option) => (
            <Box
              key={option.label}
              display="flex"
              alignItems="center"
              flexDirection="column"
              justifyContent="center"
            >
              <Box
                component="button"
                onClick={() => {
                  void handleShareClick(option)
                }}
                sx={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={option.icon}
                  alt={option.label}
                  sx={{ width: 40, aspectRatio: '1' }}
                />
              </Box>
              <Typography variant="subtitle2" color="grey.500">
                {option.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box>
          <Typography variant="subtitle1" mb="6px">
            Invitation Link
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: '8px',
              cursor: 'pointer',
              border: 1,
              borderColor: 'grey.200',
            }}
            onClick={() => {
              void copyCurrentUrl()
            }}
          >
            <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
              {shareUrl}
            </Typography>
            {isLinkCopy ? (
              <IconChecks size={16} stroke={2} />
            ) : (
              <IconCopy size={16} stroke={2} />
            )}
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={showInstagramToast}
        autoHideDuration={4000}
        onClose={() => setShowInstagramToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowInstagramToast(false)} severity="info" sx={{ width: '100%' }}>
          Link copied! Paste it into Instagram to share.
        </Alert>
      </Snackbar>
    </PlainModal>
  )
}

export default ShareInvite
